// ATEM SuperSource border and compositing model, per pixel.
//
// Measured on the studio's ATEM Constellation 8K (per-SuperSource border, one
// border setting for all four boxes); see docs/atem-supersource-mapping.md
// section 6. The reference renderer this is ported from matched 13.9 million
// captured pixels with 99.9 % within 2 code values (max 6).
//
// Everything works on the cropped window from ssBoxGeometry() and on pixel
// centres (x + 0.5, y + 0.5). Distances are Chebyshev distances to the window
// rectangle, positive inside, so corners are square and bevel facets meet on
// the diagonal (mitred).
//
// Compositing (bottom to top, box 4 first):
//   C   = picture of the topmost box whose window contains the pixel (hard)
//   out = art (background art) or black (foreground art)
//   out = out*(1 - A) + border*A*(1 - Q) + C*A*Q      for each box
// With foreground art the boxes are keyed hard over black, without border.
import { layoutBorder, layoutBorderProtocol, } from "./types/layouts.js";
import { SS_BORDER_RANGES, SS_SOFT_INSET, SS_SOFT_RAMP, ssBorderEdges, ssBorderWidthPx, ssRampWidth, } from "./supersource-geometry.js";
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
// ============ Colour ============
/** R'G'B' 0..1 of the border colour: standard HSL, identical to CSS hsl(). */
export function ssBorderRgb(border) {
    const hue = Math.round(border.hue ?? 0);
    const h = (((hue % 3600) + 3600) % 3600) / 3600;
    const s = clamp01((border.saturation ?? 0) / 1000);
    const l = clamp01((border.luma ?? 0) / 1000);
    if (s === 0)
        return [l, l, l];
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const t = (x) => {
        const v = ((x % 1) + 1) % 1;
        if (v < 1 / 6)
            return p + (q - p) * 6 * v;
        if (v < 0.5)
            return q;
        if (v < 2 / 3)
            return p + (q - p) * (2 / 3 - v) * 6;
        return p;
    };
    return [t(h + 1 / 3), t(h), t(h - 1 / 3)];
}
/** CSS colour of the border (exact for a browser preview). */
export function ssBorderCss(border) {
    const hue = (((Math.round(border.hue ?? 0) % 3600) + 3600) % 3600) / 10;
    const sat = clamp01((border.saturation ?? 0) / 1000) * 100;
    const luma = clamp01((border.luma ?? 0) / 1000) * 100;
    return `hsl(${hue} ${sat}% ${luma}%)`;
}
/** #RRGGBB of the border colour. */
export function ssBorderHex(border) {
    const hex = (v) => Math.round(clamp01(v) * 255)
        .toString(16)
        .padStart(2, "0");
    return `#${ssBorderRgb(border).map(hex).join("")}`.toUpperCase();
}
/**
 * #RRGGBB -> the switcher's HSL: hue 0..3599 (0.1 deg), saturation and luma
 * 0..1000 (0.1 %). The inverse of {@link ssBorderRgb} up to the 8-bit
 * rounding of the hex colour. Null for anything but #RRGGBB / RRGGBB.
 */
export function ssHexToBorderHsl(color) {
    const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
    if (!m)
        return null;
    const value = parseInt(m[1], 16);
    const r = ((value >> 16) & 0xff) / 255;
    const g = ((value >> 8) & 0xff) / 255;
    const b = (value & 0xff) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    const l = (max + min) / 2;
    let hue = 0;
    if (delta !== 0) {
        if (max === r)
            hue = ((g - b) / delta) % 6;
        else if (max === g)
            hue = (b - r) / delta + 2;
        else
            hue = (r - g) / delta + 4;
        hue *= 60;
        if (hue < 0)
            hue += 360;
    }
    const sat = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1) || 1);
    return {
        hue: Math.round(hue * 10) % 3600,
        saturation: Math.round(Math.min(sat, 1) * 1000),
        luma: Math.round(l * 1000),
    };
}
/** R'G'B' (0..1) -> BT.709 limited-range 8-bit Y'CbCr (unrounded). */
export function ssRgbToYcc([r, g, b]) {
    const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return [
        16 + 219 * y,
        128 + (224 * (b - y)) / 1.8556,
        128 + (224 * (r - y)) / 1.5748,
    ];
}
/** BT.709 limited-range 8-bit Y'CbCr -> R'G'B' (0..1, not clipped). */
export function ssYccToRgb([Y, Cb, Cr]) {
    const y = (Y - 16) / 219;
    const pb = (Cb - 128) / 224;
    const pr = (Cr - 128) / 224;
    const r = y + 1.5748 * pr;
    const b = y + 1.8556 * pb;
    const g = (y - 0.2126 * r - 0.0722 * b) / 0.7152;
    return [r, g, b];
}
/** 8-bit luma code of an R'G'B' (0..1) colour. */
export function ssLumaCode(r, g, b) {
    return 16 + 219 * (0.2126 * r + 0.7152 * g + 0.0722 * b);
}
// ============ Bevel ============
/** Facing of the outer facet per side (0 = up, 90 = left, ...). */
const FACING = { left: 90, right: 270, top: 0, bottom: 180 };
/**
 * Shading factor of a bevel facet facing `facetDeg` (0 = up, 90 = left,
 * 180 = down, 270 = right): multiplies the border's 8-bit Y code.
 */
export function ssBevelGain(directionUnits, altitudeUnits, facetDeg) {
    const alt = Math.min(100, Math.max(10, Math.round(altitudeUnits)));
    const dir = (((Math.round(directionUnits) % 3600) + 3600) % 3600) / 10;
    const cot = alt >= 100 ? 0 : 1 / Math.tan((alt * 0.9 * Math.PI) / 180);
    return 0.9657 + 0.1251 * cot * Math.cos(((dir - facetDeg) * Math.PI) / 180);
}
/**
 * Coverage of one box: soft edge, outer/inner border with softness, and the
 * bevel. `artForeground` (artOption 1) makes the window hard and drops the
 * border, whatever the border settings.
 */
export function ssBoxCoverage(window, border, artForeground = false) {
    const { left: L, right: R, top: T, bottom: B } = window;
    if (artForeground) {
        return {
            window,
            borderYcc: null,
            reachOutside: 0,
            reachInside: 0,
            at(px, py, out) {
                const inside = px >= L && px < R && py >= T && py < B;
                out.A = out.P = inside ? 1 : 0;
                out.borderY = 0;
                return out;
            },
        };
    }
    const edges = ssBorderEdges(border);
    if (!edges || !border) {
        // Border off: 50 % point 1.533 px inside the window, ramp 2.045 px
        const half = SS_SOFT_RAMP / 2;
        return {
            window,
            borderYcc: null,
            reachOutside: Math.max(0, half - SS_SOFT_INSET),
            reachInside: SS_SOFT_INSET + half,
            at(px, py, out) {
                const cx = px + 0.5;
                const cy = py + 0.5;
                const d = Math.min(cx - L, R - cx, cy - T, B - cy);
                const a = clamp01(0.5 + (d - SS_SOFT_INSET) / SS_SOFT_RAMP);
                out.A = a;
                out.P = a;
                out.borderY = 0;
                return out;
            },
        };
    }
    const { outer: o, inner: e, outerRamp: ra, innerRamp: rq } = edges;
    const base = ssRgbToYcc(ssBorderRgb(border));
    const Y0 = base[0];
    const mode = Math.min(3, Math.max(0, Math.round(border.bevel ?? 0)));
    const position = Math.min(100, Math.max(0, Math.round(border.bevelPosition ?? 0)));
    const split = e - (position / 100) * (e - o);
    const rb = ssRampWidth(border.bevelSoftness ?? 0);
    const dir = border.lightSourceDirection ?? 0;
    const alt = border.lightSourceAltitude ?? 10;
    // Each facet is clipped to 1..254 before the two are blended.
    const clipY = (g) => Math.min(254, Math.max(1, Y0 * g));
    // Per side (0 left, 1 right, 2 top, 3 bottom): outer / inner facet luma.
    // mode 1 (in/out): both facets shaded; 2 (in): outer flat; 3 (out): inner flat
    const sides = ["left", "right", "top", "bottom"];
    const yOut = sides.map((side) => clipY(mode === 2 ? 1 : ssBevelGain(dir, alt, FACING[side])));
    const yIn = sides.map((side) => clipY(mode === 3 ? 1 : ssBevelGain(dir, alt, FACING[side] + 180)));
    // Distances of the current pixel per side, and the facet depths (the top
    // and bottom sides one line late)
    const ds = [0, 0, 0, 0];
    const depth = [0, 0, 0, 0];
    const shade = (i) => {
        const t = clamp01(0.5 + (depth[i] - split) / rb); // 0 = outer, 1 = inner
        return yOut[i] * (1 - t) + yIn[i] * t;
    };
    return {
        window,
        borderYcc: base,
        reachOutside: Math.max(0, ra / 2 - o),
        reachInside: Math.max(o + ra / 2, e + rq / 2),
        at(px, py, out) {
            const cx = px + 0.5;
            const cy = py + 0.5;
            const dl = cx - L;
            const dr = R - cx;
            const dt = cy - T;
            const db = B - cy;
            const d = Math.min(dl, dr, dt, db);
            const A = clamp01(0.5 + (d - o) / ra);
            const Q = clamp01(0.5 + (d - e) / rq);
            out.A = A;
            out.P = A * Q;
            if (mode === 0 || A <= 0 || Q >= 1) {
                out.borderY = Y0;
                return out;
            }
            // Facet shading. The mitre (which side a pixel belongs to) uses the
            // pixel's own distances, blended across the diagonal with the bevel
            // softness ramp. The depth (outer vs inner facet) of the top and bottom
            // sides is evaluated one line late (except on frame line 0): their
            // split sits 1 px lower than the formula.
            const late = py > 0 ? 1 : 0;
            ds[0] = depth[0] = dl;
            ds[1] = depth[1] = dr;
            ds[2] = dt;
            ds[3] = db;
            depth[2] = dt - late;
            depth[3] = db + late;
            // The two nearest sides; ties keep the order left, right, top, bottom
            let s1 = 0;
            let s2 = 1;
            if (ds[1] < ds[0]) {
                s1 = 1;
                s2 = 0;
            }
            for (let i = 2; i < 4; i++) {
                if (ds[i] < ds[s1]) {
                    s2 = s1;
                    s1 = i;
                }
                else if (ds[i] < ds[s2]) {
                    s2 = i;
                }
            }
            const w2 = clamp01(0.5 + (ds[s1] - ds[s2]) / rb);
            out.borderY = shade(s1) * (1 - w2) + (w2 > 0 ? shade(s2) * w2 : 0);
            return out;
        },
    };
}
/**
 * Composite a whole SuperSource at one pixel (8-bit Y'CbCr, unrounded). The
 * reference form of the model; the preview renders the same maths over a
 * frame buffer. With `artForeground` the result is what the foreground art
 * is keyed over (boxes hard over black).
 */
export function ssCompositePixel(boxes, px, py, art, artForeground = false) {
    const inside = (w) => px >= w.left && px < w.right && py >= w.top && py < w.bottom;
    const top = boxes.find((b) => inside(b.coverage.window));
    if (artForeground)
        return top ? [...top.picture(px, py)] : [16, 128, 128];
    let out = [...art(px, py)];
    const C = top ? top.picture(px, py) : [...out];
    const cov = { A: 0, P: 0, borderY: 0 };
    for (let i = boxes.length - 1; i >= 0; i--) {
        const { coverage } = boxes[i];
        coverage.at(px, py, cov);
        if (cov.A <= 0)
            continue;
        const bc = coverage.borderYcc
            ? [cov.borderY, coverage.borderYcc[1], coverage.borderYcc[2]]
            : C;
        out = out.map((v, j) => v * (1 - cov.A) + bc[j] * (cov.A - cov.P) + C[j] * cov.P);
    }
    return out;
}
// ============ Border state ============
/** Store / atem-connection field name of each border field. */
export const SS_BORDER_LIB_FIELDS = {
    bevel: "borderBevel",
    outerWidth: "borderOuterWidth",
    innerWidth: "borderInnerWidth",
    outerSoftness: "borderOuterSoftness",
    innerSoftness: "borderInnerSoftness",
    bevelSoftness: "borderBevelSoftness",
    bevelPosition: "borderBevelPosition",
    hue: "borderHue",
    saturation: "borderSaturation",
    luma: "borderLuma",
    lightSourceDirection: "borderLightSourceDirection",
    lightSourceAltitude: "borderLightSourceAltitude",
};
const BEVEL_NAMES = [
    "none",
    "in-out",
    "in",
    "out",
];
/** Bevel name of a bevel value (0..3; 4+ is stored as 3). */
export function ssBevelName(bevel) {
    return BEVEL_NAMES[Math.min(3, Math.max(0, Math.round(bevel)))];
}
/** Bevel value (0..3) of a bevel name. */
export function ssBevelValue(name) {
    return Math.max(0, BEVEL_NAMES.indexOf(name));
}
/**
 * Full border state from protocol values (missing fields count as the
 * switcher's minimum), with the derived px, colour and degree values.
 */
export function ssBorderState(border) {
    const v = (field) => {
        const [lo, hi] = SS_BORDER_RANGES[field];
        const raw = Math.round(border[field] ?? lo);
        return Number.isFinite(raw) ? Math.min(hi, Math.max(lo, raw)) : lo;
    };
    const state = {
        enabled: border.enabled,
        bevel: v("bevel"),
        outerWidth: v("outerWidth"),
        innerWidth: v("innerWidth"),
        outerSoftness: v("outerSoftness"),
        innerSoftness: v("innerSoftness"),
        bevelSoftness: v("bevelSoftness"),
        bevelPosition: v("bevelPosition"),
        hue: v("hue"),
        saturation: v("saturation"),
        luma: v("luma"),
        lightSourceDirection: v("lightSourceDirection"),
        lightSourceAltitude: v("lightSourceAltitude"),
    };
    return {
        ...state,
        outerWidthPx: ssBorderWidthPx(state.outerWidth),
        innerWidthPx: ssBorderWidthPx(state.innerWidth),
        color: ssBorderHex(state),
        bevelMode: ssBevelName(state.bevel),
        lightSourceDirectionDeg: state.lightSourceDirection / 10,
        lightSourceAltitudeDeg: Math.round(state.lightSourceAltitude * 9) / 10,
    };
}
/**
 * A layout's border in protocol units, for previews: what the push sends,
 * with the colour as HSL. Fields the layout leaves out (the switcher keeps
 * them) count as the minimum; without a colour the preview uses white.
 */
export function ssLayoutBorder(border) {
    const hsl = (border.color && ssHexToBorderHsl(border.color)) || {
        hue: 0,
        saturation: 0,
        luma: 1000,
    };
    return { ...layoutBorderProtocol(border), ...hsl };
}
/**
 * The border the switcher draws for a layout, for fitting its boxes (see
 * ssFitBox `border`): null when the border is off or the art is in the
 * foreground (the switcher draws no border then). Fields the layout leaves
 * out count as 0.
 */
export function ssLayoutDrawnBorder(layout) {
    const border = layoutBorder(layout);
    if (!border.enabled)
        return null;
    if (layout.art_enabled && layout.art_placement === "foreground")
        return null;
    return ssLayoutBorder(border);
}
